import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatDate } from "../utils/utils";
import { useNavigate } from "react-router-dom";
import BuscarAuxiliar from "./components/BuscarAuxiliar";
import { ACTION_PERMISSIONS, hasActionPermission } from "../utils/authPermissions";

const normalizeMonto = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeFecha = (value) => value ? String(value).slice(0, 10) : "";

const getFechaLocalActual = () => {
    const ahora = new Date();
    ahora.setMinutes(ahora.getMinutes() - ahora.getTimezoneOffset());
    return ahora.toISOString().slice(0, 10);
};

const EstadoAcuerdoBadge = ({ estado }) => (
    estado === 1 ? (
        <span className="badge border border-success text-success bg-success bg-opacity-10 px-3 py-2">Activo</span>
    ) : (
        <span className="badge border border-danger text-danger bg-danger bg-opacity-10 px-3 py-2">Inactivo</span>
    )
);

const DetalleAcuerdo = ({ isOpen, onClose, details }) => {
    if (!isOpen || !details) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        {/* Encabezado */}
                        <div className="modal-header bg-light border-bottom">
                            <h5 className="modal-title">
                                Detalles del Acuerdo #{details.idAcuerdo}
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>

                        {/* Cuerpo */}
                        <div className="modal-body">
                            {/* Estado y encabezado */}
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1">
                                        <strong>Paciente:</strong> {details.paciente}
                                    </p>
                                    <p className="mb-0">
                                        <strong>Auxiliar:</strong> {details.auxiliar}
                                    </p>
                                </div>
                                <div><EstadoAcuerdoBadge estado={details.estado} /></div>
                            </div>

                            {/* Fechas */}
                            <div className="row mt-3">
                                <div className="col-md-6">
                                    <div className="card p-3 shadow-sm border-0 bg-light">
                                        <p className="mb-1 text-muted">Fecha de Inicio</p>
                                        <p className="fw-bold mb-0">
                                            {formatDate(details.fechaInicio)}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card p-3 shadow-sm border-0 bg-light">
                                        <p className="mb-1 text-muted">Fecha de Fin</p>
                                        <p className="fw-bold mb-0">
                                            {details.fechaFin ? formatDate(details.fechaFin) : "—"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Valores */}
                            <div className="row mt-3">
                                <div className="col-md-4">
                                    <div className="card p-3 border-0 bg-light text-center">
                                        <p className="text-muted mb-1">Valor Turno</p>
                                        <h6 className="fw-bold mb-0">
                                            ${details.valorTurno?.toLocaleString("es-CL")}
                                        </h6>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card p-3 border-0 bg-light text-center">
                                        <p className="text-muted mb-1">Movilización</p>
                                        <h6 className="fw-bold mb-0">
                                            ${details.valorMovilizacion?.toLocaleString("es-CL")}
                                        </h6>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card p-3 border-0 bg-light text-center">
                                        <p className="text-muted mb-1">Colación</p>
                                        <h6 className="fw-bold mb-0">
                                            ${details.valorColacion?.toLocaleString("es-CL")}
                                        </h6>
                                    </div>
                                </div>
                            </div>

                            {/* Observaciones */}
                            <div className="mt-4">
                                <h6 className="text-muted">Observación</h6>
                                <div className="border rounded p-3 bg-light" style={{ minHeight: "70px" }}>
                                    {details.observacion && details.observacion.trim() !== ""
                                        ? details.observacion
                                        : <span className="text-secondary fst-italic">Sin observaciones</span>}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-backdrop fade show"></div>
        </>
    );
};

const ModalCerrarAcuerdo = ({ isOpen, onClose, acuerdo, onSubmit }) => {
    const [fecha, setFecha] = useState(() => new Date().toISOString().split('T')[0]);
    const [observacion, setObservacion] = useState('');
    const [turno, setTurno] = useState('dia'); // 'dia' o 'noche'

    // Resetear valores cuando se abre el modal o cambia el acuerdo
    useEffect(() => {
        if (isOpen) {
            setFecha(new Date().toISOString().split('T')[0]);
            setObservacion('');
            setTurno('noche');
        }
    }, [isOpen, acuerdo]);

    const handleConfirm = () => {
        // Enviar turno junto con los demás campos
        onSubmit({
            idAcuerdo: acuerdo?.idAcuerdo,
            fecha,
            observacion,
            turno,
        });
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Cerrar Acuerdo</h5>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Cerrar"></button>
                        </div>
                        <div className="modal-body">
                            <p>¿Deseas cerrar el acuerdo #{acuerdo?.idAcuerdo}?</p>

                            <div className="mb-3">
                                <label htmlFor="fechaCierre" className="form-label">Fecha de cierre</label>
                                <input
                                    id="fechaCierre"
                                    type="date"
                                    className="form-control"
                                    value={fecha}
                                    onChange={(e) => setFecha(e.target.value)}
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="turnoSelect" className="form-label">Periodo</label>
                                <select
                                    id="turnoSelect"
                                    className="form-select"
                                    value={turno}
                                    onChange={(e) => setTurno(e.target.value)}
                                >
                                    <option value="dia">Día</option>
                                    <option value="noche">Noche</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="observacion" className="form-label">Observación</label>
                                <textarea
                                    id="observacion"
                                    className="form-control"
                                    value={observacion}
                                    onChange={(e) => setObservacion(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                            <button className="btn btn-danger" onClick={handleConfirm}>Confirmar cierre</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-backdrop fade show" />
        </>
    );
};

const ModalActualizarAcuerdo = ({ isOpen, onClose, acuerdo, onSubmit }) => {
    const puedeEditarValoresTurno = hasActionPermission(ACTION_PERMISSIONS.EDITAR_ACUERDO_VALOR_TURNO);
    // Estados
    const [fechaInicio, setFechaInicio] = useState("");
    const [auxiliar, setAuxiliar] = useState(null);
    const [turno, setTurno] = useState("");
    const [movilizacion, setMovilizacion] = useState("");
    const [colacion, setColacion] = useState("");
    const [tipoColacion, setTipoColacion] = useState("3");
    const [observacion, setObservacion] = useState("");

    // Cargar datos del acuerdo recibido
    useEffect(() => {
        if (acuerdo) {
            setFechaInicio(getFechaLocalActual());
            setTurno(normalizeMonto(acuerdo.valorTurno));
            setMovilizacion(normalizeMonto(acuerdo.valorMovilizacion));
            setColacion(normalizeMonto(acuerdo.valorColacion));
            setTipoColacion(acuerdo.tipo_colacion || "3");
            setObservacion(acuerdo.observacion || "");
            setAuxiliar(null); // Limpia el auxiliar seleccionado nuevo
        }
    }, [acuerdo]);

    // Guardar cambios
    const handleGuardar = () => {
        // Si no se seleccionó uno nuevo, usar el auxiliar actual del acuerdo
        const idAuxiliarFinal = auxiliar?.id || acuerdo.idAuxiliar;

        if (!idAuxiliarFinal) {
            alert("No se pudo determinar el auxiliar. Revisa los datos.");
            return;
        }

        if (!fechaInicio) {
            alert("Selecciona la fecha de inicio del nuevo acuerdo.");
            return;
        }

        const fechaAcuerdoActual = normalizeFecha(acuerdo.fechaInicio);
        if (fechaAcuerdoActual && fechaInicio < fechaAcuerdoActual) {
            alert("La fecha del nuevo acuerdo no puede ser anterior al inicio del acuerdo actual.");
            return;
        }

        const guardarActualizacion = () => onSubmit({
            idAcuerdo: acuerdo.idAcuerdo,
            fechaInicio,
            auxiliar: idAuxiliarFinal,
            turno: normalizeMonto(turno),
            movilizacion: normalizeMonto(movilizacion),
            colacion: normalizeMonto(colacion),
            tipo_colacion: tipoColacion,
            observacion: observacion.trim(),
        });

        if (fechaAcuerdoActual && fechaInicio === fechaAcuerdoActual) {
            const confirmado = window.confirm(
                "La fecha del nuevo acuerdo es igual a la fecha de inicio del acuerdo actual. " +
                "Esto aplicará los cambios desde el comienzo del acuerdo. ¿Desea continuar?"
            );
            if (!confirmado) return;
        }

        guardarActualizacion();
    };

    if (!isOpen || !acuerdo) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        {/* Encabezado */}
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Actualizar Acuerdo #{acuerdo.idAcuerdo}
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>

                        {/* Cuerpo */}
                        <div className="modal-body">
                            <div className="mb-2">
                                <p><strong>Paciente:</strong> {acuerdo.paciente}</p>
                                <p><strong>Auxiliar actual:</strong> {acuerdo.auxiliar}</p>
                                <p>
                                    <strong>Estado:</strong>{" "}
                                    <EstadoAcuerdoBadge estado={acuerdo.estado} />
                                </p>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label">Fecha de inicio del acuerdo actual</label>
                                    <input type="date" className="form-control" value={normalizeFecha(acuerdo.fechaInicio)} disabled />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Fecha de inicio del nuevo acuerdo</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fechaInicio}
                                        min={normalizeFecha(acuerdo.fechaInicio)}
                                        onChange={(e) => setFechaInicio(e.target.value)}
                                    />
                                </div>
                                <div className="col-12 mt-2">
                                    <div className="form-text">Los cambios se aplicarán a los turnos desde la fecha del nuevo acuerdo.</div>
                                </div>
                            </div>

                            {/* Buscar Auxiliar (opcional) */}
                            <div className="mb-3">
                                <label className="form-label">Cambiar Auxiliar (opcional)</label>
                                <BuscarAuxiliar onSelect={setAuxiliar} />
                                <div className="mt-2 text-muted">
                                    {auxiliar
                                        ? <>Nuevo auxiliar: <strong>{auxiliar.nombre}</strong></>
                                        : <>Se mantendrá el auxiliar actual: <strong>{acuerdo.auxiliar}</strong></>}
                                </div>
                            </div>

                            {/* Valores */}
                            <div className="row">
                                {!puedeEditarValoresTurno && (
                                    <div className="col-12">
                                        <div className="alert alert-warning py-2 mb-2 small">
                                            Valor del turno en solo lectura para su perfil.
                                        </div>
                                    </div>
                                )}
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Valor Turno</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={turno}
                                            readOnly={!puedeEditarValoresTurno}
                                            onChange={(e) => setTurno(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Movilización</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={movilizacion}
                                            onChange={(e) => setMovilizacion(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Colación</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={colacion}
                                            onChange={(e) => setColacion(e.target.value)}
                                        />
                                        <label className="form-label mt-2 small text-muted">Aplica en</label>
                                        <select
                                            className="form-select"
                                            value={tipoColacion}
                                            onChange={(e) => setTipoColacion(e.target.value)}
                                        >
                                            <option value="1">Día</option>
                                            <option value="2">Noche</option>
                                            <option value="3">Ambos</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="col-md-4 d-none">
                                    <div className="mb-3">
                                        <label className="form-label">Tipo Colación</label>
                                        <select
                                            className="form-select"
                                            value={tipoColacion}
                                            onChange={(e) => setTipoColacion(e.target.value)}
                                        >
                                            <option value="1">Día</option>
                                            <option value="2">Noche</option>
                                            <option value="3">Ambos</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Observación */}
                            <div className="mb-3">
                                <label className="form-label">Observación</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={observacion}
                                    onChange={(e) => setObservacion(e.target.value)}
                                ></textarea>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button className="btn btn-primary" onClick={handleGuardar}>
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-backdrop fade show"></div>
        </>
    );
};


const AcuerdosMutuos = () => {
    const [resultados, setResultados] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [detalleAcuerdo, setDetalleAcuerdo] = useState(null);
    const [modalCerrar, setModalCerrar] = useState(false);
    const [acuerdoCerrar, setAcuerdoCerrar] = useState(null);
    const [modalActualizar, setModalActualizar] = useState(false);
    const [acuerdoActualizar, setAcuerdoActualizar] = useState(null);
    const [filtroEstado, setFiltroEstado] = useState("todos");
    const navigate = useNavigate();

    useEffect(() => {
        async function traeAcuerdos() {
            try {
                const response = await axiosInstance.get("/acuerdo-mutuo/lista");
                setResultados(response.data.data);
            } catch (error) {
                console.error("Error al buscar acuerdos: ", error);
            }
        }
        traeAcuerdos();
    }, []);

    const verDetalle = (acuerdo) => {
        setDetalleAcuerdo(acuerdo);
        setIsModalOpen(true);
    };

    const abrirCerrarAcuerdo = (acuerdo) => {
        setAcuerdoCerrar(acuerdo);
        setModalCerrar(true);
    };

    const cerrarAcuerdo = async ({ idAcuerdo, fecha, observacion, turno }) => {
        try {
            await axiosInstance.post(`/acuerdo-mutuo/cerrar`, { idAcuerdo, fecha, observacion, turno });
            setModalCerrar(false);
            setAcuerdoCerrar(null);
            const response = await axiosInstance.get("/acuerdo-mutuo/lista");
            setResultados(response.data.data);
        } catch (error) {
            console.error("Error al cerrar acuerdo: ", error);
        }
    };

    const abrirActualizarAcuerdo = (acuerdo) => {
        setAcuerdoActualizar(acuerdo);
        setModalActualizar(true);
    };

    const actualizarAcuerdo = async (data) => {
        try {
            await axiosInstance.post(`/acuerdo-mutuo/actualizar`, data);
            setModalActualizar(false);
            const response = await axiosInstance.get("/acuerdo-mutuo/lista");
            setResultados(response.data.data);
        } catch (error) {
            console.error("Error al actualizar acuerdo: ", error);
        }
    };

    const columns = [
        { name: "idAcuerdo", title: "Id", width: 100 },
        { name: "paciente", title: "Paciente", width: 200 },
        { name: "auxiliar", title: "Auxiliar", width: 200 },
        { name: 'fechaInicio', title: 'Fecha Inicio', render: (row) => formatDate(row.fechaInicio), width: 100 },
        { name: 'fechaFin', title: 'Fecha Fin', render: (row) => formatDate(row.fechaFin), width: 100 },
        { name: "observacion", title: "Observación", width: 100 },
        { name: "estado", title: "Estado", render: (row) => <EstadoAcuerdoBadge estado={row.estado} />, width: 120 },
        {
            name: 'acciones',
            title: 'Acciones',
            render: (row) => (
                <div className="d-flex gap-2">
                    <button className="btn btn-primary btn-sm" onClick={() => verDetalle(row)}>Ver</button>
                    {row.estado === 1 && (
                        <>
                            <button className="btn btn-success btn-sm" onClick={() => abrirActualizarAcuerdo(row)}>Actualizar</button>
                            <button className="btn btn-danger btn-sm" onClick={() => abrirCerrarAcuerdo(row)}>Cerrar</button>
                        </>
                    )}
                </div>
            ),
            width: 200
        },
    ];

    const resultadosFiltrados = resultados.filter((row) => {
        if (filtroEstado === "activos") return row.estado === 1;
        if (filtroEstado === "cerrados") return row.estado !== 1;
        return true;
    });

    return (
        <div className="container mt-5">
            <div className="card p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="mb-0">Acuerdos Mutuos</h3>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/acuerdo-mutuo')}
                    >
                        <i className="bi bi-plus-circle me-2"></i>
                        Crear Acuerdo
                    </button>
                </div>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="text-muted small fw-bold">Estado:</span>
                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            type="button"
                            className={`btn ${filtroEstado === "todos" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setFiltroEstado("todos")}
                        >
                            Ambos
                        </button>
                        <button
                            type="button"
                            className={`btn ${filtroEstado === "activos" ? "btn-success" : "btn-outline-success"}`}
                            onClick={() => setFiltroEstado("activos")}
                        >
                            Activos
                        </button>
                        <button
                            type="button"
                            className={`btn ${filtroEstado === "cerrados" ? "btn-danger" : "btn-outline-danger"}`}
                            onClick={() => setFiltroEstado("cerrados")}
                        >
                            Inactivos
                        </button>
                    </div>
                </div>
                <GdTable
                    columns={columns}
                    data={resultadosFiltrados}
                    defaultItemsPerPage={100}
                    itemsPerPageOptions={[5, 10, 25, 50, 100]}
                    initialSort={[0, "desc"]}
                />
            </div>

            <DetalleAcuerdo
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                details={detalleAcuerdo}
            />

            <ModalCerrarAcuerdo
                isOpen={modalCerrar}
                onClose={() => setModalCerrar(false)}
                acuerdo={acuerdoCerrar}
                onSubmit={cerrarAcuerdo}
            />

            <ModalActualizarAcuerdo
                isOpen={modalActualizar}
                onClose={() => setModalActualizar(false)}
                acuerdo={acuerdoActualizar}
                onSubmit={actualizarAcuerdo}
            />
        </div>
    );
};

export default AcuerdosMutuos;
